/**
 * useConfirm — hook + provider for imperative confirm dialogs.
 *
 * Setup (once, in App.js or index.js):
 *   import { ConfirmProvider } from "./utils/useConfirm";
 *   <ConfirmProvider><App /></ConfirmProvider>
 *
 * Usage anywhere:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, subtitle, danger });
 *   if (ok) { ... }
 */
import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import NovalaConfirmDialog from "../components/NovalaConfirmDialog";

export interface ConfirmOptions {
  title?: string;
  subtitle?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  hideCancel?: boolean;
  danger?: boolean;
}

export type ConfirmFunction = (opts: ConfirmOptions) => Promise<boolean>;

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
}

const ConfirmContext = React.createContext<ConfirmFunction | null>(null);

interface ConfirmProviderProps {
  children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps): React.ReactElement {
  const [state, setState] = useState<ConfirmState>({ isOpen: false });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm: ConfirmFunction = useCallback(
    (opts: ConfirmOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setState({
          isOpen: true,
          title: opts.title,
          subtitle: opts.subtitle,
          message: opts.message,
          confirmLabel: opts.confirmLabel,
          cancelLabel: opts.cancelLabel,
          hideCancel: opts.hideCancel,
          danger: opts.danger,
        });
      });
    },
    []
  );

  function handleConfirm(): void {
    if (resolverRef.current) resolverRef.current(true);
    resolverRef.current = null;
    setState({ isOpen: false });
  }

  function handleCancel(): void {
    if (resolverRef.current) resolverRef.current(false);
    resolverRef.current = null;
    setState({ isOpen: false });
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <NovalaConfirmDialog
        isOpen={state.isOpen}
        title={state.title}
        subtitle={state.subtitle}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        hideCancel={state.hideCancel}
        danger={state.danger}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFunction {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to window.confirm if provider not mounted (defensive)
    return (opts: ConfirmOptions): Promise<boolean> => {
      return Promise.resolve(
        window.confirm((opts && opts.title) || "Are you sure?")
      );
    };
  }
  return ctx;
}